import { useMemo, useState } from 'react';

import { BridgeEventMethod, FullMessage } from '@scaleaq/sitecontrol-lib';

import { useBridgeRegistration } from '../registerAndSubscribe';
import { isSameData } from './useGetSensorsDataByEventAndIds/isSameData.ts';
import { useMultiSensorEventSubscription } from './useGetSensorsDataByEventAndIds/useMultiSensorEventSubscription.ts';

/**
 * Get sensor data by event and ids
 * @param ids The ids of the inventory items
 * @param eventName e.g. "GioChannelChanged"
 */
export const useGetSensorsDataByEventAndIds = <T>(ids: string[], eventName: string) => {
  const [results, setResults] = useState<{ [key: string]: T }>({});

  const listenerId = useMemo(
    () => `useGetSensorsDataByEventAndIds-${eventName}-${ids.join('-')}`,
    [eventName, ids]
  );
  // Always, first register the event handler
  useBridgeRegistration({
    bridgeEventMethod: BridgeEventMethod.FullMessage,
    eventCallback: (message: FullMessage) => {
      const { id, listenerIds } = message;
      // A message without listenerIds is not addressed to this listener.
      if (!listenerIds?.includes(listenerId)) return;

      const data = message.data as T;
      // Same state object: React skips reconciling children when a message repeats data we already hold.
      setResults(prev => {
        if (Object.hasOwn(prev, id) && isSameData(prev[id], data)) return prev;
        return { ...prev, [id]: data };
      });
    },
  });

  // Then subscribe to the event(s)
  const { isLoading } = useMultiSensorEventSubscription(ids, eventName, listenerId);

  return { results, isLoading };
};
